<template>
  <div
    class="preset-dialog"
    role="presentation"
    @mousedown="onMaskMouseDown"
    @mouseup="onMaskMouseUp"
  >
    <section class="preset-dialog__panel" role="dialog" aria-modal="true" :aria-label="$t('art_preset_title')">
      <button class="preset-dialog__close" type="button" :aria-label="$t('cancel')" @click="onClose">
        <WorkbenchIcon name="x" :size="16" />
      </button>

      <header class="preset-dialog__header">
        <h2>{{ $t('art_preset_title') }}</h2>
      </header>
      <p class="preset-dialog__description">{{ dialogDescription }}</p>

      <div class="preset-lang-bar">
        <div class="preset-lang-field">
          <span class="preset-lang-field__label">{{ $t('art_preset_source_lang') }}</span>
          <div class="preset-lang-select" :class="{ 'is-open': openSelect === 'source' }">
            <button type="button" class="preset-lang-select__trigger" :disabled="importing" aria-haspopup="listbox" :aria-expanded="openSelect === 'source'" :aria-label="$t('art_preset_source_lang') + ' ' + langName(sourceLang)" @click.stop="toggleSelect('source')">
              <strong>{{ langName(sourceLang) }}</strong><small>{{ sourceLang }}</small>
              <WorkbenchIcon name="chevron-down" :size="14" />
            </button>
            <ul v-if="openSelect === 'source'" class="preset-lang-select__menu" role="listbox">
              <li v-for="code in PRESET_TERM_LANGS" :key="code" role="option" :aria-selected="code === sourceLang">
                <button type="button" class="preset-lang-select__option" :class="{ 'is-selected': code === sourceLang }" @click.stop="pickSource(code)">
                  <strong>{{ langName(code) }}</strong><small>{{ code }}</small>
                  <WorkbenchIcon v-if="code === sourceLang" name="check" :size="14" />
                </button>
              </li>
            </ul>
          </div>
        </div>
        <span class="preset-lang-arrow" aria-hidden="true"><WorkbenchIcon name="arrow-right" :size="16" /></span>
        <div class="preset-lang-field">
          <span class="preset-lang-field__label">{{ $t('art_preset_target_lang') }}</span>
          <div class="preset-lang-select" :class="{ 'is-open': openSelect === 'target' }">
            <button type="button" class="preset-lang-select__trigger" :disabled="importing" aria-haspopup="listbox" :aria-expanded="openSelect === 'target'" :aria-label="$t('art_preset_target_lang') + ' ' + langName(targetLang)" @click.stop="toggleSelect('target')">
              <strong>{{ langName(targetLang) }}</strong><small>{{ targetLang }}</small>
              <WorkbenchIcon name="chevron-down" :size="14" />
            </button>
            <ul v-if="openSelect === 'target'" class="preset-lang-select__menu" role="listbox">
              <li v-for="code in PRESET_TERM_LANGS" :key="code" role="option" :aria-selected="code === targetLang" :aria-disabled="code === sourceLang">
                <button type="button" class="preset-lang-select__option" :disabled="code === sourceLang" :class="{ 'is-selected': code === targetLang, 'is-disabled': code === sourceLang }" @click.stop="pickTarget(code)">
                  <strong>{{ langName(code) }}</strong><small>{{ code }}</small>
                  <WorkbenchIcon v-if="code === targetLang" name="check" :size="14" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <p class="preset-lang-tip">{{ $t('art_preset_lang_tip') }}</p>

      <div class="preset-dialog__body">
        <section class="preset-list">
          <header class="preset-list__header">
            <button v-if="importableCount > 0" type="button" :disabled="!canImport" @click="onChooseAll">
              <span class="preset-check" :aria-pressed="allChosen">
                <WorkbenchIcon v-if="allChosen" name="check" :size="11" />
              </span>
              {{ $t('art_preset_select_all') }}
            </button>
            <span v-else>{{ $t('art_table_title1') }}</span>
            <span>{{ $t('art_table_title2') }}</span>
          </header>

          <div class="preset-list__content">
            <div v-if="listPhase === 'loading'" class="preset-state">
              <span class="preset-spinner" />
              <span>{{ $t('art_preset_loading') }}</span>
            </div>
            <div v-else-if="listPhase === 'error'" class="preset-state">
              <WorkbenchIcon name="alert-triangle" :size="20" />
              <span>{{ listError }}</span>
              <button type="button" @click="loadList">{{ $t('art_batch_retry_failed') }}</button>
            </div>
            <div v-else-if="items.length === 0" class="preset-state">
              <WorkbenchIcon name="book-marked" :size="24" />
              <span>{{ $t('art_preset_empty') }}</span>
            </div>
            <template v-else>
              <button
                v-for="item in items"
                :key="item.presetKey"
                class="preset-list__row"
                :class="{ 'is-active': current?.presetKey === item.presetKey, 'is-checked': Boolean(selectedMap[item.presetKey]), 'is-imported': item.exist }"
                type="button"
                @click="onPreview(item)"
              >
                <span
                  class="preset-check"
                  :aria-pressed="Boolean(selectedMap[item.presetKey])"
                  :aria-disabled="!canImport"
                  @click.stop="onToggle(item)"
                >
                  <WorkbenchIcon v-if="selectedMap[item.presetKey]" name="check" :size="11" />
                </span>
                <span class="preset-list__name" :title="item.termName">{{ item.termName }}</span>
                <span v-if="item.exist" class="preset-list__tag">{{ $t('art_preset_imported') }}</span>
                <span class="preset-list__count">{{ item.entryNum }}</span>
              </button>
            </template>
          </div>
        </section>

        <section class="preset-preview">
          <header class="preset-preview__header"><strong>{{ $t('art_preset_preview_title') }}</strong></header>
          <div class="preset-preview__content">
            <div v-if="!current" class="preset-state">{{ $t('art_preset_preview_empty') }}</div>
            <div v-else-if="previewPhase === 'loading'" class="preset-state">
              <span class="preset-spinner" />
              <span>{{ $t('art_preset_preview_loading') }}</span>
            </div>
            <template v-else>
              <strong class="preset-preview__summary">
                {{ $t('art_preset_preview_summary', { name: current.termName, count: current.entryNum }) }}
              </strong>
              <div class="preset-preview__table">
                <div class="preset-preview__row preset-preview__row--header">
                  <span>{{ $t('art_preset_source_column', { lang: sourceLang }) }}</span>
                  <span>{{ $t('art_preset_translation_column', { lang: targetLang }) }}</span>
                </div>
                <div class="preset-preview__entries">
                  <div v-for="(entry, index) in previewEntries.slice(0, 5)" :key="`${sourceLang}-${targetLang}-${entry.entryKey}-${index}`" class="preset-preview__row">
                    <span :title="entry.entryKey">{{ entry.entryKey }}</span>
                    <span :title="entry.entryValue">{{ entry.entryValue }}</span>
                  </div>
                </div>
              </div>
              <p class="preset-dialog__notice">{{ $t('art_preset_reimport_tip') }}</p>
            </template>
          </div>
        </section>
      </div>

      <footer class="preset-dialog__footer">
        <button class="preset-dialog__button preset-dialog__button--secondary" type="button" :disabled="importing" @click="onClose">
          {{ $t('cancel') }}
        </button>
        <button
          class="preset-dialog__button preset-dialog__button--primary"
          type="button"
          :disabled="!canImport || selectedKeys.length === 0 || importing"
          @click="onImport"
        >
          <span v-if="importing" class="preset-spinner preset-spinner--light" />
          {{ $t('art_preset_import_btn').replace('*', String(selectedKeys.length)) }}
        </button>
      </footer>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AccountServer from '@/services/AccountServer'
import type { PresetTermEntry, PresetTermHeader } from '@/services/AccountServer/types'
import WorkbenchIcon from '@/components/Workbench/WorkbenchIcon.vue'
import { ERROR_I18N } from '@/composables/useGlossaryBatch'
import Toast from '@/utils/toast'
import { PRESET_TERM_LANGS, PRESET_TERM_LANG_I18N, defaultPresetLangPair, fallbackTargetLang, type PresetTermLang } from '@/utils/presetTermLang'

const emit = defineEmits<{
  (event: 'onClose', hasImported?: boolean): void
}>()
const props = withDefaults(defineProps<{
  mode?: 'business' | 'personal'
  canImport?: boolean
}>(), {
  mode: 'business',
  canImport: true,
})
const { t, te, locale } = useI18n()

const listPhase = ref<'loading' | 'ready' | 'error'>('loading')
const listError = ref('')
const items = ref<PresetTermHeader[]>([])
const selectedMap = ref<Record<string, boolean>>({})
const current = ref<PresetTermHeader | null>(null)
const previewPhase = ref<'loading' | 'ready'>('ready')
const previewEntries = ref<PresetTermEntry[]>([])
const previewCache = new Map<string, PresetTermEntry[]>()
const initialPair = defaultPresetLangPair(locale.value)
const sourceLang = ref<PresetTermLang>(initialPair.sourceLang)
const targetLang = ref<PresetTermLang>(initialPair.targetLang)
const openSelect = ref<'source' | 'target' | null>(null)
const pairKey = computed(() => `${sourceLang.value}>${targetLang.value}`)

function langName(code: PresetTermLang) { return t(PRESET_TERM_LANG_I18N[code]) }
function toggleSelect(which: 'source' | 'target') {
  if (importing.value) return
  openSelect.value = openSelect.value === which ? null : which
}
function closeSelect() { openSelect.value = null }
function pickSource(code: PresetTermLang) {
  if (importing.value) return
  openSelect.value = null
  if (code === sourceLang.value) return
  sourceLang.value = code
  if (targetLang.value === code) targetLang.value = fallbackTargetLang(code)
  onPairChanged()
}
function pickTarget(code: PresetTermLang) {
  if (importing.value) return
  if (code === sourceLang.value || code === targetLang.value) return
  openSelect.value = null
  targetLang.value = code
  onPairChanged()
}
/** 语言对变了：已导入标记按语言对重新拉，勾选与预览作废 */
function onPairChanged() {
  selectedMap.value = {}
  current.value = null
  previewEntries.value = []
  loadList()
}
const importing = ref(false)
const hasImported = ref(false)
const disposed = ref(false)
const maskMouseDown = ref(false)

const selectedKeys = computed(() => Object.keys(selectedMap.value).filter((key) => selectedMap.value[key]))
const importableCount = computed(() => items.value.filter((item) => !item.exist).length)
const allChosen = computed(() => {
  const importableKeys = items.value.filter((item) => !item.exist).map((item) => item.presetKey)
  return importableKeys.length > 0 && importableKeys.every((key) => selectedMap.value[key])
})
const dialogDescription = computed(() => t(props.mode === 'business' ? 'art_preset_desc_business' : 'art_preset_desc_personal'))

async function settle(promise: Promise<any>): Promise<any> {
  try {
    return await promise
  } catch (error: any) {
    if (error && typeof error === 'object' && 'code' in error) return error
    return { code: 'NETWORK_ERROR', message: error?.message || String(error) }
  }
}

function errorText(result: any): string {
  const key = ERROR_I18N[String(result?.code || '')]
  if (key && (te(key) || te(key, 'en'))) return t(key)
  if (/^\d+$/.test(String(result?.code || '')) && result?.message) return result.message
  return t('art_batch_err_unknown')
}

async function loadList(options: { preserveContent?: boolean } = {}) {
  const preserveContent = options.preserveContent === true && listPhase.value === 'ready'
  if (!preserveContent) listPhase.value = 'loading'
  const requestPair = pairKey.value
  const result = await settle(AccountServer.getPresetTermList({ sourceLang: sourceLang.value, targetLang: targetLang.value }))
  if (disposed.value || requestPair !== pairKey.value) return
  if (!result || result.code !== '1') {
    // 导入成功后的后台校准失败时保留已经乐观更新的列表，避免内容清空和成功提示后紧接错误态。
    if (preserveContent) return
    listError.value = errorText(result)
    listPhase.value = 'error'
    return
  }

  const list = Array.isArray(result.list) ? result.list as PresetTermHeader[] : []
  items.value = list
  selectedMap.value = Object.fromEntries(
    list
      .filter((item) => selectedMap.value[item.presetKey])
      .map((item) => [item.presetKey, true]),
  )
  listPhase.value = 'ready'
}

function onToggle(item: PresetTermHeader) {
  if (!props.canImport) return
  selectedMap.value = {
    ...selectedMap.value,
    [item.presetKey]: !selectedMap.value[item.presetKey],
  }
}

function onChooseAll() {
  if (!props.canImport) return
  const next = { ...selectedMap.value }
  items.value.filter((item) => !item.exist).forEach((item) => {
    if (allChosen.value) delete next[item.presetKey]
    else next[item.presetKey] = true
  })
  selectedMap.value = next
}

async function onPreview(item: PresetTermHeader) {
  current.value = item
  const cacheKey = `${pairKey.value}:${item.presetKey}`
  const cached = previewCache.get(cacheKey)
  if (cached) {
    previewEntries.value = cached
    previewPhase.value = 'ready'
    return
  }

  previewPhase.value = 'loading'
  const requestPair = pairKey.value
  const result = await settle(AccountServer.getPresetTermEntryList({ presetKey: item.presetKey, sourceLang: sourceLang.value, targetLang: targetLang.value }))
  if (disposed.value || current.value?.presetKey !== item.presetKey || requestPair !== pairKey.value) return
  if (!result || result.code !== '1') {
    Toast(errorText(result))
    previewEntries.value = []
    previewPhase.value = 'ready'
    return
  }

  const list = Array.isArray(result.list) ? result.list as PresetTermEntry[] : []
  previewCache.set(cacheKey, list)
  previewEntries.value = list
  previewPhase.value = 'ready'
}

async function onImport() {
  if (!props.canImport || selectedKeys.value.length === 0 || importing.value) return
  importing.value = true
  const keys = [...selectedKeys.value]
  const result = await settle(AccountServer.importPresetTerms({ presetKeys: keys, sourceLang: sourceLang.value, targetLang: targetLang.value }))
  if (disposed.value) return
  importing.value = false

  if (result?.code === '1') {
    hasImported.value = true
    const importedKeys = new Set(keys)
    items.value = items.value.map((item) => (
      importedKeys.has(item.presetKey) ? { ...item, exist: true } : item
    ))
    selectedMap.value = {}
    Toast(t('art_preset_import_success').replace('*', String(keys.length)), 'success')
    await loadList({ preserveContent: true })
    return
  }

  Toast(errorText(result))
  if (result?.code === '10026') await loadList()
}

function onMaskMouseDown(event: MouseEvent) {
  maskMouseDown.value = event.target === event.currentTarget
}

function onMaskMouseUp(event: MouseEvent) {
  const shouldClose = maskMouseDown.value && event.target === event.currentTarget && !importing.value
  maskMouseDown.value = false
  if (shouldClose) onClose()
}

function onClose() {
  if (importing.value) return
  emit('onClose', hasImported.value)
}

function onKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape') return
  if (openSelect.value) { openSelect.value = null; return }
  onClose()
}

onMounted(() => {
  loadList()
  window.addEventListener('keydown', onKeydown)
  window.addEventListener('click', closeSelect)
})

onUnmounted(() => {
  disposed.value = true
  window.removeEventListener('keydown', onKeydown)
  window.removeEventListener('click', closeSelect)
})
</script>

<style scoped lang="scss">
@keyframes preset-spin {
  to { transform: rotate(360deg); }
}

.preset-dialog {
  position: fixed;
  z-index: 2000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.5);
}

.preset-dialog__panel {
  position: relative;
  box-sizing: border-box;
  display: flex;
  width: min(900px, 94vw);
  height: min(814px, calc(100vh - 48px));
  max-height: 94vh;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  overflow: hidden;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.22);
}

.preset-dialog__close {
  position: absolute;
  top: 22px;
  right: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #8f959e;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.preset-dialog__header {
  display: flex;
  min-height: 24px;
  align-items: center;
  justify-content: center;
  padding: 0 36px;

  h2 {
    margin: 0;
    color: #1f2329;
    font-size: 17px;
    font-weight: 800;
    line-height: 24px;
  }
}

.preset-dialog__description {
  margin: 0;
  color: #51565d;
  font-size: 12.5px;
  line-height: 19px;
  text-align: center;
}

.preset-lang-bar {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 标签和下拉直接参与语言栏的 flex 排布，两个下拉按 flex:1 1 0 等宽 */
.preset-lang-field {
  display: contents;
}

.preset-lang-field__label {
  flex: 0 0 auto;
  color: #51565d;
  font-size: 12.5px;
  font-weight: 600;
}

.preset-lang-arrow {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  padding: 0 6px;
  color: #8f959e;
}

.preset-lang-select {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
}

.preset-lang-select__trigger {
  box-sizing: border-box;
  display: flex;
  width: 100%;
  height: 34px;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  color: #1f2329;
  text-align: left;
  background: #fff;
  border: 1px solid #d9dee5;
  border-radius: 8px;
  cursor: pointer;

  strong { font-size: 12.5px; font-weight: 600; }
  small { flex: 1; color: #8f959e; font-size: 11.5px; }
  .iconfont, .workbench-icon { color: #8f959e; transition: transform 0.15s ease; }
}

.preset-lang-select.is-open .preset-lang-select__trigger {
  border-color: #2b7de9;

  .workbench-icon {
    color: #2b7de9;
    transform: rotate(180deg);
  }
}

.preset-lang-select__menu {
  position: absolute;
  z-index: 5;
  top: 42px;
  right: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 6px;
  list-style: none;
  background: #fff;
  border: 1px solid #e3e7ec;
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12);

  li { border-radius: 6px; }
}

.preset-lang-select__option {
  display: flex;
  width: 100%;
  height: 31px;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  color: #1f2329;
  font: inherit;
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: 6px;
  cursor: pointer;

  strong { font-size: 12.5px; font-weight: 600; }
  small { flex: 1; color: #8f959e; font-size: 11px; }

  &:hover,
  &.is-selected {
    color: #2b7de9;
    background: #f5f9ff;
  }

  &.is-selected strong { font-weight: 700; }

  &:disabled {
    color: #b8bec7;
    background: transparent;
    cursor: not-allowed;

    small { color: #c9ced6; }
  }
}

.preset-lang-tip {
  margin: -10px 0 0;
  color: #8f959e;
  font-size: 11px;
  line-height: 15px;
}

@media (max-width: 760px) {
  .preset-lang-bar { flex-direction: column; align-items: stretch; }
  .preset-lang-select { flex: 0 0 auto; }
  .preset-lang-arrow { display: none; }
}

.preset-dialog__body {
  display: grid;
  min-height: 0;
  flex: 1;
  overflow: hidden;
  grid-template-columns: 370px minmax(0, 1fr);
  gap: 16px;
}

.preset-list,
.preset-preview {
  display: flex;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e3e7ec;
  border-radius: 8px;
}

.preset-list__header,
.preset-preview__header {
  box-sizing: border-box;
  display: flex;
  min-height: 37px;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  flex: 0 0 auto;
  color: #51565d;
  font-size: 12.5px;
  font-weight: 600;
  background: #f5f9ff;

  button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0;
    color: #1f2329;
    font: inherit;
    background: transparent;
    border: 0;
    cursor: pointer;

    &:disabled { color: #b8c0cc; cursor: not-allowed; }
  }

  strong {
    color: #1f2329;
    font-weight: 600;
  }
}

.preset-list__content,
.preset-preview__content {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  overflow: hidden;
}

.preset-list__content {
  overflow-y: auto;
}

.preset-preview__content {
  gap: 8px;
  padding: 12px;
}

.preset-list__row {
  box-sizing: border-box;
  display: flex;
  min-height: 36px;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  color: #1f2329;
  font-size: 12.5px;
  text-align: left;
  background: #fff;
  border: 0;
  border-bottom: 1px solid #f2f5f9;
  cursor: pointer;

  &:hover { background: #f4f8ff; }
  &.is-active,
  &.is-checked { background: #f8fbff; }
  &.is-checked .preset-list__name {
    color: #2b7de9;
    font-weight: 600;
  }
}

.preset-check {
  box-sizing: border-box;
  display: inline-flex;
  width: 15px;
  height: 15px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: #fff;
  border: 1px solid #c3c9d1;
  border-radius: 3px;

  &[aria-pressed='true'] {
    background: #2b7de9;
    border-color: #2b7de9;
  }

  &[aria-disabled='true'] {
    background: #f5f6f8;
    border-color: #d9dee5;
  }
}

.preset-list__name {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  font-size: 12.5px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preset-list__tag {
  flex: 0 0 auto;
  padding: 1px 6px;
  color: #8f959e;
  font-size: 10.5px;
  font-weight: 700;
  line-height: 15px;
  background: #f2f4f7;
  border-radius: 4px;
}

.preset-list__count {
  flex: 0 0 auto;
  width: 48px;
  margin-left: auto;
  font-size: 12.5px;
  text-align: right;
}

.preset-preview__table {
  display: flex;
  min-height: 0;
  flex: 0 1 auto;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #eef1f5;
  border-radius: 6px;
}

.preset-preview__row {
  box-sizing: border-box;
  display: grid;
  min-height: 49px;
  align-items: flex-start;
  padding: 8px 12px;
  color: #1f2329;
  font-size: 12px;
  border-bottom: 1px solid #f2f5f9;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  column-gap: 12px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.preset-preview__row--header {
  min-height: 32px;
  color: #51565d;
  font-size: 11.5px;
  font-weight: 600;
  line-height: 15px;
  background: #fafbfc;
}

.preset-preview__entries {
  min-height: 0;
  overflow: hidden;

  .preset-preview__row:last-child { border-bottom: 0; }
}

.preset-preview__summary {
  display: block;
  padding: 0;
  color: #1f2329;
  font-size: 12.5px;
  line-height: 18px;
}

.preset-state {
  display: flex;
  min-height: 180px;
  align-items: center;
  justify-content: center;
  flex: 1;
  flex-direction: column;
  gap: 9px;
  padding: 24px;
  color: #8b95a5;
  font-size: 13px;
  text-align: center;

  button {
    padding: 0;
    color: #2b7de9;
    font: inherit;
    background: transparent;
    border: 0;
    cursor: pointer;
  }
}

.preset-spinner {
  display: inline-block;
  width: 17px;
  height: 17px;
  border: 2px solid #cfe0f8;
  border-top-color: #2b7de9;
  border-radius: 50%;
  animation: preset-spin 0.8s linear infinite;
}

.preset-spinner--light {
  width: 14px;
  height: 14px;
  border-color: rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
}

.preset-dialog__footer {
  display: flex;
  gap: 16px;
}

.preset-dialog__notice {
  margin: 0;
  color: #8f959e;
  font-size: 11px;
  line-height: 17px;
}

.preset-dialog__button {
  display: inline-flex;
  height: 44px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 18px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  flex: 1;

  &:disabled { cursor: not-allowed; }
}

.preset-dialog__button--secondary {
  color: #2b7de9;
  background: #fff;
  border: 1px solid #2b7de9;

  &:disabled { opacity: 0.48; }
}

.preset-dialog__button--primary {
  color: #fff;
  font-weight: 700;
  background: #2b7de9;
  border: 1px solid #2b7de9;

  &:disabled {
    opacity: 1;
    background: #dcebfb;
    border-color: #dcebfb;
  }
}

@media (max-width: 760px) {
  .preset-dialog { padding: 12px; }
  .preset-dialog__panel { height: auto; max-height: 94vh; padding: 20px; }
  .preset-dialog__body {
    min-height: 0;
    overflow-y: auto;
    grid-template-columns: 1fr;
  }
  .preset-list,
  .preset-preview { min-height: 260px; }
  .preset-dialog__footer { flex-wrap: wrap; }
  .preset-dialog__button { flex: 1; }
}
</style>
